import { showNotification } from '@mantine/notifications';
import { Icon } from '@repo/foundations';
import type { MutationFunction } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { isString } from 'lodash-es';
import type { PartialWithoutId } from '../../../lib/typescript';
import { apiClient, getEndpoints } from '../../common';
import type { ISecodaEntity, Namespace } from '../../types';
import { secodaEntitiesQueryKeyFactory } from './constants';

export type BulkUpdateData = Array<{
	id: string;
	data: PartialWithoutId<ISecodaEntity>;
}>;

export type RequestData = {
	data: BulkUpdateData;
	signal?: AbortSignal;
};

export function getDefaultBulkUpdateFn(
	namespace: Namespace
): MutationFunction<string[], RequestData> {
	return async ({ data, signal }) => {
		const baseUrl = getEndpoints(namespace).root();
		const url = `${baseUrl}bulk_update`;
		const { data: responseData } = await apiClient.post<string[]>(url, data, {
			signal,
		});
		return responseData;
	};
}

export type SuccessMessage = {
	title: string;
	message: string;
};

function useBulkUpdateSecodaEntities(
	onSuccessMessage: SuccessMessage = {
		title: 'Resources updated',
		message: 'The resources have been updated with selected metadata',
	}
) {
	const { namespace } = secodaEntitiesQueryKeyFactory;
	const mutationFn = getDefaultBulkUpdateFn(namespace);

	return useMutation<string[], unknown, RequestData>({
		mutationFn,
		onError: (err) => {
			const message = (err as AxiosError).response?.data;
			showNotification({
				title: 'Error updating resources',
				message: isString(message)
					? message
					: 'An error occurred while updating the resources',
				color: 'red',
				icon: <Icon name="alertCircle" color="text/critical-on-fill/default" />,
			});
		},
		onSuccess: () => {
			showNotification({
				title: onSuccessMessage.title,
				message: onSuccessMessage.message,
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
			});
		},
	});
}

export default useBulkUpdateSecodaEntities;
